/**
 * Erasmus+ Project Generator - Main App
 * React + TypeScript interface for intelligent project generation
 */

import React, { useState, useEffect } from 'react';
import { Box, Container, Stepper, Step, StepLabel, Button, AppBar, Toolbar, Typography, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { 
  Logout as LogoutIcon, 
  Email as EmailIcon, 
  Folder as FolderIcon, 
  AdminPanelSettings as AdminIcon, 
  Storefront as StorefrontIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Bolt as BoltIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import { Analytics } from '@vercel/analytics/react';
import BasicInfoForm from './components/BasicInfoForm';
import ProjectContentForm from './components/ProjectContentForm';
import WorkPackagesForm from './components/WorkPackagesForm';
import PreviewPanel from './components/PreviewPanel';
import ExportPanel from './components/ExportPanel';
import HomePage from './components/HomePage';
import ContactPage from './components/ContactPage';
import MyProjectsPage from './components/MyProjectsPage';
import AdminPage from './components/AdminPage';
import ProjectsShowcasePage from './components/ProjectsShowcasePage';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import SubscriptionPlans from './components/SubscriptionPlans';
import BillingProfile from './components/BillingProfile';
import PaymentSuccess from './components/PaymentSuccess';
import Paywall from './components/Paywall';
import LandingPage from './components/LandingPage';
import GeneratedProjectsPage from './components/GeneratedProjectsPage';
import InsightsPage from './components/InsightsPage';
import AdminProjectsViewPage from './components/AdminProjectsViewPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TerminiCondizioni from './components/TerminiCondizioni';
import CookiePolicy from './components/CookiePolicy';
import CookieBanner from './components/CookieBanner';
import { useAuth } from './contexts/AuthContext';
import { ProjectData, GenerationStatus, SubscriptionStatus } from './types';
import { API_URL } from './config';
import './App.css';

// Admin users list
const ADMIN_EMAILS = [
  'ingmarinuzzi@gmail.com',
  'alessandro@marinuzzi.com',
  'al.marinuzzi@gmail.com'
];

const isAdminUser = (email: string | undefined): boolean => {
  return email !== undefined && ADMIN_EMAILS.includes(email);
};

const steps = [
  'Basic Information',
  'Project Content',
  'Work Packages',
  'Preview & Refine',
  'Export PDF'
];

function App() {
  const { isAuthenticated, user, login, logout, token } = useAuth();
  const [showHomePage, setShowHomePage] = useState(false);  // "Generate" page (wizard intro)
  const [showContacts, setShowContacts] = useState(false);
  const [showMyProjects, setShowMyProjects] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showShowcase, setShowShowcase] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showLanding, setShowLanding] = useState(!isAuthenticated);  // Landing/Info is now the default homepage
  const [showGeneratedProjects, setShowGeneratedProjects] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showAdminProjects, setShowAdminProjects] = useState(false);
  const [showPrivacyPolicyPage, setShowPrivacyPolicyPage] = useState(false);
  const [showTerminiCondizioniPage, setShowTerminiCondizioniPage] = useState(false);
  const [showCookiePolicyPage, setShowCookiePolicyPage] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [projectData, setProjectData] = useState<ProjectData>({
    id: '',
    basic_info: null,
    content: {},
    work_packages: [],
    status: 'draft'
  });
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>({
    isGenerating: false,
    progress: 0,
    currentSection: ''
  });

  // Load subscription status when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadSubscriptionStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  // Check for URL parameters (payment success, page routing)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for payment success
    if (urlParams.get('session_id')) {
      setShowPaymentSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Check for direct page routing
    const page = urlParams.get('page');
    if (page === 'generate') {
      setShowLanding(false);
      setShowHomePage(true);
    } else if (page === 'info') {
      // Keep for backwards compatibility - redirects to landing (now default)
      setShowLanding(true);
    } else if (page === 'plans') {
      setShowLanding(false);
      setShowSubscription(true);
    } else if (page === 'contact') {
      setShowLanding(false);
      setShowContacts(true);
    } else if (page === 'projects') {
      setShowLanding(false);
      setShowGeneratedProjects(true);
    } else if (page === 'insights') {
      setShowLanding(false);
      setShowInsights(true);
    } else if (page === 'privacy-policy') {
      setShowLanding(false);
      setShowPrivacyPolicyPage(true);
    } else if (page === 'termini-condizioni') {
      setShowLanding(false);
      setShowTerminiCondizioniPage(true);
    } else if (page === 'cookie-policy') {
      setShowLanding(false);
      setShowCookiePolicyPage(true);
    }
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
      }
    } catch (err) {
      console.warn('Could not load subscription status:', err);
    }
  };

  // Handle login success
  const handleLoginSuccess = (token: string, user: { email: string; name: string }) => {
    login(token, user);
    setShowLogin(false);
    setShowRegister(false);
    setShowHomePage(false);
  };

  // Show dedicated privacy-policy page (URL ?page=privacy-policy or link from footer/register/contact)
  if (showPrivacyPolicyPage) {
    return (
      <PrivacyPolicy
        onBack={() => {
          setShowPrivacyPolicyPage(false);
          window.history.replaceState({}, '', window.location.pathname || '/');
        }}
      />
    );
  }

  // Show dedicated termini-condizioni page (URL ?page=termini-condizioni)
  if (showTerminiCondizioniPage) {
    return (
      <TerminiCondizioni
        onBack={() => {
          setShowTerminiCondizioniPage(false);
          window.history.replaceState({}, '', window.location.pathname || '/');
        }}
      />
    );
  }

  // Show dedicated cookie-policy page (URL ?page=cookie-policy)
  if (showCookiePolicyPage) {
    return (
      <CookiePolicy
        onBack={() => {
          setShowCookiePolicyPage(false);
          window.history.replaceState({}, '', window.location.pathname || '/');
        }}
      />
    );
  }

  // Show login page
  if (showLogin) {
    return (
      <LoginForm
        onLoginSuccess={handleLoginSuccess}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
      />
    );
  }

  // Show register page
  if (showRegister) {
    return (
      <RegisterForm
        onRegisterSuccess={handleLoginSuccess}
        onSwitchToLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
    );
  }

  // Show showcase page
  if (showShowcase) {
    return <ProjectsShowcasePage onBack={() => setShowShowcase(false)} />;
  }

  // Show landing page (Info) - This is now the homepage for non-authenticated users
  if (showLanding) {
    return (
      <LandingPage 
        isHomepage={!isAuthenticated}
        onStartProject={() => {
          setShowLanding(false);
          setShowHomePage(true);  // Go to Generate page
        }}
        onShowLogin={() => {
          setShowLanding(false);
          setShowLogin(true);
        }}
        onBack={() => setShowLanding(false)}
        onShowContact={() => {
          setShowLanding(false);
          setShowContacts(true);
        }}
        onShowProjects={() => {
          setShowLanding(false);
          setShowGeneratedProjects(true);
        }}
        onShowInsights={() => {
          setShowLanding(false);
          setShowInsights(true);
        }}
      />
    );
  }

  // Show generated projects page
  if (showGeneratedProjects) {
    return (
      <GeneratedProjectsPage 
        onBack={() => setShowGeneratedProjects(false)}
        onSubscribe={() => {
          setShowGeneratedProjects(false);
          setShowSubscription(true);
        }}
        onShowContact={() => {
          setShowGeneratedProjects(false);
          setShowContacts(true);
        }}
        onShowLanding={() => {
          // Reset all page states and show landing
          setShowGeneratedProjects(false);
          setShowInsights(false);
          setShowContacts(false);
          setShowSubscription(false);
          setShowHomePage(false);
          setShowLanding(true);
        }}
      />
    );
  }

  // Show insights page
  if (showInsights) {
    return (
      <InsightsPage 
        onBack={() => setShowInsights(false)}
        onShowContact={() => {
          setShowInsights(false);
          setShowContacts(true);
        }}
        onShowProjects={() => {
          setShowInsights(false);
          setShowGeneratedProjects(true);
        }}
        onShowLanding={() => {
          // Reset all page states and show landing
          setShowInsights(false);
          setShowGeneratedProjects(false);
          setShowContacts(false);
          setShowSubscription(false);
          setShowHomePage(false);
          setShowLanding(true);
        }}
      />
    );
  }

  // Show payment success page
  if (showPaymentSuccess) {
    return (
      <PaymentSuccess 
        onContinue={() => {
          setShowPaymentSuccess(false);
          loadSubscriptionStatus(); // Reload subscription status
        }} 
      />
    );
  }

  // Show subscription plans page
  if (showSubscription) {
    return (
      <SubscriptionPlans 
        onBack={() => setShowSubscription(false)}
        onShowContact={() => {
          setShowSubscription(false);
          setShowContacts(true);
        }}
      />
    );
  }

  // Show billing profile page
  if (showBilling) {
    return (
      <BillingProfile 
        onBack={() => setShowBilling(false)}
        onShowContact={() => {
          setShowBilling(false);
          setShowContacts(true);
        }}
      />
    );
  }

  // Show contacts page
  if (showContacts) {
    return <ContactPage onBack={() => setShowContacts(false)} />;
  }

  // Show my projects page
  if (showMyProjects) {
    return <MyProjectsPage onBack={() => setShowMyProjects(false)} />;
  }

  // Show admin page
  if (showAdmin) {
    return <AdminPage onBack={() => setShowAdmin(false)} />;
  }

  // Show admin projects view page (VIEW all templates)
  if (showAdminProjects) {
    return (
      <AdminProjectsViewPage 
        onBack={() => setShowAdminProjects(false)}
        onGenerateProject={(project) => {
          // Pre-fill the wizard with project data and navigate to wizard
          setProjectData({
            ...projectData,
            content: {
              problem_statement: project.problem_statement,
              main_objective: project.main_objective,
              target_groups: project.target_groups,
              expected_results: project.expected_results,
              innovation_elements: project.innovation_elements,
              eu_priorities: project.eu_priorities
            }
          });
          setShowAdminProjects(false);
          setActiveStep(1); // Go to Project Content step
        }}
      />
    );
  }

  // Show home page (Generate intro) for non-authenticated users who clicked "Start Project"
  if (showHomePage && !isAuthenticated) {
    return <HomePage 
      onStartProject={() => setShowHomePage(false)} 
      onShowContacts={() => setShowContacts(true)}
      onShowLogin={() => setShowLogin(true)}
      onShowInfo={() => {
        setShowHomePage(false);
        setShowLanding(true);
      }}
      onShowProjects={() => setShowGeneratedProjects(true)}
      onShowInsights={() => setShowInsights(true)}
    />;
  }

  const handleNext = async () => {
    const nextStep = activeStep + 1;
    
    // If going to Export step (4) and no project ID, try to load last project
    if (nextStep === 4 && !projectData.id && token) {
      try {
        const response = await fetch(`${API_URL}/api/projects/latest`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const latest = await response.json();
          if (latest.project_id) {
            setProjectData({ ...projectData, id: latest.project_id, sections: latest.sections || {} });
          }
        }
      } catch (error) {
        console.error('Could not load latest project:', error);
      }
    }
    
    setActiveStep(nextStep);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleGenerate = async () => {
    console.log('🚀 handleGenerate called');
    
    // Get auth token from context or localStorage
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      alert('Devi effettuare il login per generare progetti.');
      return;
    }

    // CHECK EXECUTION LIMIT FIRST
    try {
      const limitCheck = await fetch(`${API_URL}/api/subscription/check-limit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (limitCheck.ok) {
        const limitResult = await limitCheck.json();
        console.log('📊 Limit check:', limitResult);
        
        if (!limitResult.can_execute) {
          // Show paywall - limit reached
          console.log('⚠️ Execution limit reached, showing paywall');
          setShowPaywall(true);
          // Reload subscription status to show updated count
          loadSubscriptionStatus();
          return;
        }
        
        // Update local subscription status with new count
        if (subscriptionStatus) {
          setSubscriptionStatus({
            ...subscriptionStatus,
            executions_used: limitResult.executions_used,
            executions_limit: limitResult.executions_limit
          });
        }
      }
    } catch (err) {
      console.warn('Could not check limit, proceeding anyway:', err);
    }

    setGenerationStatus({ isGenerating: true, progress: 0, currentSection: '' });
    
    try {
      // Prepare data in backend expected format INCLUDING work_packages!
      const requestData = {
        basic_info: projectData.basic_info,
        problem_statement: projectData.content?.problem_statement || '',
        main_objective: projectData.content?.main_objective || '',
        target_groups: projectData.content?.target_groups || [],
        expected_results: projectData.content?.expected_results || [],
        innovation_elements: projectData.content?.innovation_elements || '',
        eu_priorities: projectData.content?.eu_priorities || [],
        work_packages: projectData.work_packages || []  // Include WPs so sections can reference them!
      };
      
      console.log('📤 Sending to /create:', requestData);
      console.log(`  ✅ Including ${projectData.work_packages?.length || 0} Work Packages`);
      
      console.log('🔑 Using token:', authToken.substring(0, 20) + '...');
      
      // Call backend API to generate content
      const response = await fetch(`${API_URL}/api/project/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Create failed:', response.status, errorText);
        if (response.status === 403 && errorText.toLowerCase().includes('limit')) {
          // Project limit reached → show paywall so user can upgrade
          console.log('⚠️ Project limit reached, showing paywall');
          setShowPaywall(true);
          loadSubscriptionStatus();
          return;
        }
        if (response.status === 403) {
          throw new Error('Authentication failed. Please logout and login again.');
        }
        throw new Error(`Create failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Project created:', result.project_id);
      setProjectData({ ...projectData, id: result.project_id });
      // Store project ID for auto-save in Step 2
      localStorage.setItem('current_project_id', result.project_id);
      
      // Start generation
      console.log('📤 Starting generation for:', result.project_id);
      const genResponse = await fetch(`${API_URL}/api/project/${result.project_id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!genResponse.ok) {
        const errorText = await genResponse.text();
        console.error('❌ Generation failed:', genResponse.status, errorText);
        throw new Error(`Generation failed: ${genResponse.status} - ${errorText}`);
      }
      
      console.log('✅ Generation started, polling status...');
      
      // Poll for status
      pollGenerationStatus(result.project_id);
      
    } catch (error) {
      console.error('❌ Generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error: ${errorMessage}`);
      setGenerationStatus({ isGenerating: false, progress: 0, currentSection: '' });
    }
  };

  const pollGenerationStatus = async (projectId: string) => {
    console.log('🔄 Starting status polling for:', projectId);
    const authToken = token || localStorage.getItem('token');
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/project/${projectId}/status`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.error('❌ Authentication failed during status check');
            clearInterval(interval);
            return;
          }
          throw new Error(`Status check failed: ${response.status}`);
        }
        
        const status = await response.json();
        
        console.log('📊 Status update:', {
          status: status.status,
          progress: status.progress,
          sections: status.sections_completed.length,
          currentSection: status.sections_completed[status.sections_completed.length - 1]
        });
        
        setGenerationStatus({
          isGenerating: status.status !== 'ready',
          progress: status.progress,
          currentSection: status.sections_completed[status.sections_completed.length - 1] || ''
        });
        
        // Update project sections in real-time to show completed sections count
        if (status.sections_completed && status.sections_completed.length > 0) {
          setProjectData(prev => {
            const updated = { ...prev };
            if (!updated.sections) {
              updated.sections = {};
            }
            // Mark completed sections (even if content not loaded yet)
            status.sections_completed.forEach((sectionId: string) => {
              if (!updated.sections![sectionId]) {
                updated.sections![sectionId] = '[Generating...]';
              }
            });
            return updated;
          });
        }
        
        if (status.status === 'ready') {
          console.log('✅ Generation complete!');
          clearInterval(interval);
          // Refresh subscription status to update counter
          loadSubscriptionStatus();
          // Load generated content
          loadProject(projectId);
        }
      } catch (error) {
        console.error('❌ Status check error:', error);
        clearInterval(interval);
      }
    }, 2000);
  };

  const loadProject = async (projectId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/project/${projectId}/preview`);
      const project = await response.json();
      setProjectData(project);
      setActiveStep(3); // Move to preview
    } catch (error) {
      console.error('Load project error:', error);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoForm
            data={projectData.basic_info}
            onChange={(basicInfo) => setProjectData({ ...projectData, basic_info: basicInfo })}
          />
        );
      case 1:
        return (
          <ProjectContentForm
            data={projectData.content}
            onChange={(content) => setProjectData({ ...projectData, content })}
          />
        );
      case 2:
        return (
          <WorkPackagesForm
            data={projectData.work_packages}
            onChange={(wps) => setProjectData({ ...projectData, work_packages: wps })}
            basicInfo={projectData.basic_info}
            content={projectData.content}
            projectId={projectData.id}
          />
        );
      case 3:
        return (
          <PreviewPanel
            project={projectData}
            generationStatus={generationStatus}
            onGenerate={handleGenerate}
          />
        );
      case 4:
        return (
          <ExportPanel
            projectId={projectData.id}
            project={projectData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box className="App">
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🎓 Erasmus+ Generator
          </Typography>
          {isAuthenticated && (
            <>
              {isAdminUser(user?.email) && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<AdminIcon />}
                    onClick={() => setShowAdmin(true)}
                    sx={{ mr: 2 }}
                  >
                    Admin
                  </Button>
                  <Button
                    color="inherit"
                    startIcon={<StorefrontIcon />}
                    onClick={() => setShowShowcase(true)}
                    sx={{ mr: 2 }}
                  >
                    Showcase
                  </Button>
                  <Button
                    color="inherit"
                    onClick={() => setShowAdminProjects(true)}
                    sx={{ 
                      mr: 2,
                      bgcolor: 'rgba(233, 69, 96, 0.2)',
                      '&:hover': { bgcolor: 'rgba(233, 69, 96, 0.3)' }
                    }}
                  >
                    👁️ VIEW
                  </Button>
                </>
              )}
              <Button
                color="inherit"
                startIcon={<FolderIcon />}
                onClick={() => setShowMyProjects(true)}
                sx={{ mr: 2 }}
              >
                MY
              </Button>
              
              {/* Subscription Status & Counter */}
              {subscriptionStatus && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Piano: {subscriptionStatus.plan_name}
                      </Typography>
                      <Typography variant="body2">
                        Generazioni: {subscriptionStatus.executions_used} / {subscriptionStatus.executions_limit}
                      </Typography>
                      {subscriptionStatus.current_period_end && (
                        <Typography variant="body2">
                          Scade: {new Date(subscriptionStatus.current_period_end).toLocaleDateString('it-IT')}
                        </Typography>
                      )}
                      {subscriptionStatus.plan === 'free' && (
                        <Typography variant="body2" sx={{ mt: 1, color: '#FFCC00' }}>
                          ⚡ Passa a un piano a pagamento per più generazioni!
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box 
                    onClick={() => setShowSubscription(true)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mr: 2,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                    }}
                  >
                    <BoltIcon sx={{ 
                      color: subscriptionStatus.plan === 'free' ? 'white' : '#FFCC00',
                      fontSize: 20 
                    }} />
                    <Box sx={{ minWidth: 60 }}>
                      <Typography variant="caption" sx={{ 
                        color: 'rgba(255,255,255,0.7)', 
                        display: 'block',
                        lineHeight: 1
                      }}>
                        {subscriptionStatus.plan_name}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'white', 
                        fontWeight: 700,
                        lineHeight: 1.2
                      }}>
                        {subscriptionStatus.executions_used}/{subscriptionStatus.executions_limit}
                      </Typography>
                    </Box>
                    {/* Progress bar */}
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(100, (subscriptionStatus.executions_used / subscriptionStatus.executions_limit) * 100)}
                      sx={{ 
                        width: 40, 
                        height: 4, 
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: subscriptionStatus.executions_used >= subscriptionStatus.executions_limit 
                            ? '#FF4444' 
                            : (subscriptionStatus.plan === 'free' ? 'white' : '#FFCC00')
                        }
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
              
              <Button
                color="inherit"
                startIcon={<CreditCardIcon />}
                onClick={() => setShowSubscription(true)}
                sx={{ mr: 1 }}
              >
                Plans
              </Button>
              <Button
                color="inherit"
                startIcon={<ReceiptIcon />}
                onClick={() => setShowBilling(true)}
                sx={{ mr: 2 }}
              >
                Billing
              </Button>
              <Button
                color="inherit"
                onClick={() => setShowGeneratedProjects(true)}
                sx={{ mr: 1 }}
              >
                Projects
              </Button>
              <Button
                color="inherit"
                onClick={() => setShowInsights(true)}
                sx={{ mr: 1 }}
              >
                Insights
              </Button>
              <Button
                color="inherit"
                startIcon={<EmailIcon />}
                onClick={() => setShowContacts(true)}
                sx={{ mr: 1 }}
              >
                Contacts
              </Button>
              <Button
                color="inherit"
                onClick={() => setShowLanding(true)}
                sx={{ mr: 2 }}
              >
                Info
              </Button>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {user?.name || user?.email}
              </Typography>
              <IconButton color="inherit" onClick={logout} title="Logout">
                <LogoutIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <h1>🎓 Erasmus+ Project Generator</h1>
          <p>Create professional 25-30 page funding applications in minutes</p>
          
          <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4, minHeight: '500px' }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer with Contacts Link and LinkedIn */}
      <Box sx={{ textAlign: 'center', py: 4, mt: 6, borderTop: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <Button
          variant="text"
          startIcon={<EmailIcon />}
          onClick={() => setShowContacts(true)}
          sx={{
            color: 'white',
            fontSize: '1rem',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Contacts
        </Button>
        <Box
          component="a"
          href="https://www.linkedin.com/in/erasmus-project-generator-2119283a5/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="LinkedIn"
          sx={{
            display: 'inline-flex',
            color: 'white',
            '&:hover': { color: 'rgba(255, 255, 255, 0.8)' }
          }}
        >
          <LinkedInIcon sx={{ fontSize: 28 }} />
        </Box>
      </Box>

      {/* Paywall Modal */}
      <Paywall
        open={showPaywall}
        status={subscriptionStatus}
        onUpgrade={() => {
          setShowPaywall(false);
          setShowSubscription(true);
        }}
        onClose={() => setShowPaywall(false)}
      />

      <Analytics />
      <CookieBanner />
    </Box>
  );
}

export default App;

